import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AppUserAvatar from '../../../components/designe/app-entity-avatar/app-user-avatar/AppUserAvatar';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import { useRegisterPageContainerError } from '../../../components/layout/page-container/page-container-error-handler';
import CommunitiesListSmall from '../../../components/reusable/communities-list/CommunitiesListSmall';
import LoadMoreFeedback from '../../../components/reusable/load-more-feedback/LoadMoreFeedback';
import NoResults from '../../../components/reusable/no-results';
import Posts from '../../../components/reusable/posts/Posts';
import UsersListSmall from '../../../components/reusable/users-list/UsersListSmall';
import withRouter from '../../../hoc/with-router';
import { AvatarSize } from '../../../shared/constants';
import { AvatarTypes } from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import UserProfileSkeleton from '../shared/user-profile-skeleton/UserProfileSkeleton';
import { useStyles } from './Home.styles';
import { useHomePage } from './useHomePage';

function Home(props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const {
    user,
    communities,
    userSubscribers,
    userSubscriptions,
    posts,
    footerRef,
    refetchAll,
    isFetchingPosts,
    postsError,
    isUserLoading,
    isRelationPanel,
    showNoResult,
    visibleRelationsCount,
    isUserError,
    userError,
  } = useHomePage();

  useRegisterPageContainerError({
    errors: isUserError && userError ? [userError] : [],
    isLoading: isUserLoading,
    hasData: Boolean(user),
    onRetry: refetchAll,
  });

  if (isUserLoading) {
    return (
      <PageContainer title={t('common.myProfile')} onRefresh={refetchAll}>
        <UserProfileSkeleton />
      </PageContainer>
    );
  }

  return (
    <PageContainer title={t('common.myProfile')} onRefresh={refetchAll}>
      <div className={classes.userInfoWrapper}>
        <div className={classes.avatar}>
          <AppUserAvatar
            entity={user}
            size={AvatarSize.l}
            type={AvatarTypes.round}
          />
        </div>
        <div className={classes.info}>
          <Typography variant="text-lg">{`${user?.firstname || ''} ${user?.lastname || ''}`}</Typography>
          <Typography variant="text-md" className={classes.description}>
            {user?.description}
          </Typography>
          <Box className={classes.stats}>
            <Typography variant="text-md" component="span">
              <Typography component="span" className={classes.statsValue}>
                {userSubscribers?.length ?? 0}
              </Typography>{' '}
              {t('users.subscribers')}
            </Typography>
            <Typography variant="text-md" component="span">
              <Typography component="span" className={classes.statsValue}>
                {userSubscriptions?.length ?? 0}
              </Typography>{' '}
              {t('users.subscriptions')}
            </Typography>
          </Box>
        </div>
      </div>
      <Box className={classes.userDetailsWrapper}>
        <Box className={classes.userPostsWrapper}>
          {showNoResult && <NoResults />}
          {!showNoResult && user?.id && (
            <Posts
              posts={posts}
              isLoading={isFetchingPosts}
              newPostBinding={{ pageUserId: user.id }}
              refreshPosts={() => refetchAll()}
            />
          )}
          <LoadMoreFeedback
            error={postsError}
            isFetching={Boolean(isFetchingPosts) && Boolean(posts?.length)}
            onRetry={() => refetchAll()}
          />
          <Box ref={footerRef} mb={1} />
        </Box>
        {!!isRelationPanel && (
          <Box className={classes.userRelationsWrapper}>
            {!!userSubscriptions?.length && (
              <Box className={classes.userRelationsContainer}>
                <Link
                  className={classes.userRelationsHeader}
                  to={Paths.users.toUserSubscriptions(user.id)}>
                  <Typography variant="h3" marginBottom={1}>
                    {t('home.mySubscriptions')}
                    <Typography
                      component="span"
                      className={classes.userRelationsCounter}>
                      {userSubscriptions.length}
                    </Typography>
                  </Typography>
                </Link>
                <UsersListSmall
                  users={userSubscriptions?.slice(0, visibleRelationsCount)}
                />
              </Box>
            )}
            {!!userSubscribers?.length && (
              <Box className={classes.userRelationsContainer}>
                <Link
                  className={classes.userRelationsHeader}
                  to={Paths.users.toUserSubscribers(user.id)}>
                  <Typography variant="h3" marginBottom={1}>
                    {t('home.mySubscribers')}
                    <Typography
                      component="span"
                      className={classes.userRelationsCounter}>
                      {userSubscribers.length}
                    </Typography>
                  </Typography>
                </Link>
                <UsersListSmall
                  users={userSubscribers?.slice(0, visibleRelationsCount)}
                />
              </Box>
            )}
            {!!communities?.length && (
              <Box className={classes.userRelationsContainer}>
                <Link
                  className={classes.userRelationsHeader}
                  to={Paths.users.toUserCommunities(user.id)}>
                  <Typography variant="h3" marginBottom={1}>
                    {t('home.myCommunities')}
                    <Typography
                      component="span"
                      className={classes.userRelationsCounter}>
                      {communities.length}
                    </Typography>
                  </Typography>
                </Link>

                <CommunitiesListSmall
                  communities={communities?.slice(0, visibleRelationsCount)}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </PageContainer>
  );
}

export default withRouter(Home);
